/**
 * AdvancedSearchBar - Buscador avanzado con filtros especiales
 * Soporta: .ability, .type, .damage, .description, .tag (incl. mega, regional)
 */
import { Search, X, SlidersHorizontal, HelpCircle, Grid3X3, TableProperties, Zap, MapPin } from 'lucide-react';
import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../ui/popover';
import { useApp } from '../../context/AppContext';
import { GENERATIONS } from '../../hooks/usePokemon';

const ALL_TYPES = [
  'normal', 'fire', 'water', 'electric', 'grass', 'ice',
  'fighting', 'poison', 'ground', 'flying', 'psychic', 'bug',
  'rock', 'ghost', 'dragon', 'dark', 'steel', 'fairy'
];

// Search filter help content
const FILTER_HELP = {
  es: [
    { filter: '.ability, .a', desc: 'Buscar por habilidad (ej: .a intimidate)' },
    { filter: '.type, .t', desc: 'Buscar por tipo (ej: .t fire)' },
    { filter: '.damage, .dmg', desc: 'Categoría de daño: physical, special' },
    { filter: '.tag', desc: 'Etiqueta: legendary, mythical, starter, mega, regional, alola, galar, hisui, paldea' },
  ],
  en: [
    { filter: '.ability, .a', desc: 'Search by ability (e.g. .a intimidate)' },
    { filter: '.type, .t', desc: 'Search by type (e.g. .t fire)' },
    { filter: '.damage, .dmg', desc: 'Damage category: physical, special' },
    { filter: '.tag', desc: 'Tag: legendary, mythical, starter, mega, regional, alola, galar, hisui, paldea' },
  ]
};

export function AdvancedSearchBar({ 
  searchQuery, 
  setSearchQuery,
  selectedType,
  setSelectedType,
  selectedGeneration,
  setSelectedGeneration,
  sortBy,
  setSortBy,
  viewMode,
  setViewMode,
  formFilter,
  setFormFilter
}) {
  const { t, language } = useApp();
  const [showFilters, setShowFilters] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  
  const helpItems = FILTER_HELP[language] || FILTER_HELP.en;
  
  return (
    <div className="sticky top-0 z-40 py-4 space-y-3" data-testid="advanced-search-bar">
      {/* Main search */}
      <div className="bg-card/80 backdrop-blur-xl border border-border rounded-2xl p-3 shadow-lg">
        <div className="flex items-center gap-2 sm:gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              type="text"
              placeholder={language === 'es' ? 'Escribe para buscar...' : 'Type to start searching...'}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              data-testid="advanced-search-input"
              className="pl-10 pr-10 h-12 bg-transparent border-none text-base sm:text-lg placeholder:text-muted-foreground/60 focus-visible:ring-violet-500"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-muted"
                data-testid="clear-search"
              >
                <X className="w-4 h-4 text-muted-foreground" />
              </button>
            )}
          </div>
          
          {/* Help popover */}
          <Popover open={showHelp} onOpenChange={setShowHelp}>
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                data-testid="search-help"
                className="h-10 w-10 sm:h-12 sm:w-12 rounded-xl shrink-0"
              >
                <HelpCircle className="w-5 h-5" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80 p-4" align="end">
              <div className="space-y-3">
                <h4 className="font-semibold text-sm">
                  {language === 'es' ? 'Filtros de búsqueda disponibles:' : 'Available search filters:'}
                </h4>
                <div className="space-y-2">
                  {helpItems.map((item, i) => (
                    <div key={i} className="text-sm">
                      <code className="text-violet-600 dark:text-violet-400 bg-muted px-1.5 py-0.5 rounded text-xs">
                        {item.filter}
                      </code>
                      <p className="text-muted-foreground text-xs mt-0.5">{item.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </PopoverContent>
          </Popover>
          
          {/* View mode toggle */}
          <div className="flex bg-muted rounded-xl p-1 shrink-0">
            <Button
              variant={viewMode === 'grid' ? 'default' : 'ghost'}
              size="icon"
              onClick={() => setViewMode('grid')}
              data-testid="view-grid"
              className={`h-8 w-8 sm:h-10 sm:w-10 rounded-lg ${viewMode === 'grid' ? 'bg-violet-600 text-white' : ''}`}
            >
              <Grid3X3 className="w-4 h-4" />
            </Button>
            <Button
              variant={viewMode === 'competitive' ? 'default' : 'ghost'}
              size="icon"
              onClick={() => setViewMode('competitive')}
              data-testid="view-competitive"
              className={`h-8 w-8 sm:h-10 sm:w-10 rounded-lg ${viewMode === 'competitive' ? 'bg-violet-600 text-white' : ''}`}
            >
              <TableProperties className="w-4 h-4" />
            </Button>
          </div>
          
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setShowFilters(!showFilters)}
            data-testid="toggle-filters"
            className={`h-10 w-10 sm:h-12 sm:w-12 rounded-xl shrink-0 ${showFilters ? 'bg-violet-100 dark:bg-violet-900/50 text-violet-600' : ''}`}
          >
            <SlidersHorizontal className="w-5 h-5" />
          </Button>
        </div>
      </div>
      
      {/* Filter panel */}
      <AnimatePresence>
        {showFilters && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <div className="bg-card/80 backdrop-blur-xl border border-border rounded-2xl p-4 shadow-lg">
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                {/* Type filter */}
                <Select value={selectedType} onValueChange={setSelectedType}>
                  <SelectTrigger data-testid="type-filter" className="h-11">
                    <SelectValue placeholder={t('allTypes')} />
                  </SelectTrigger>
                  <SelectContent className="z-50">
                    <SelectItem value="all" data-testid="type-option-all">{t('allTypes')}</SelectItem>
                    {ALL_TYPES.map(type => (
                      <SelectItem key={type} value={type} data-testid={`type-option-${type}`}>
                        {t(`types.${type}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                {/* Generation filter */}
                <Select value={selectedGeneration} onValueChange={setSelectedGeneration}>
                  <SelectTrigger data-testid="generation-filter" className="h-11">
                    <SelectValue placeholder={t('allGenerations')} />
                  </SelectTrigger>
                  <SelectContent className="z-50">
                    <SelectItem value="all" data-testid="gen-option-all">{t('allGenerations')}</SelectItem>
                    {Object.entries(GENERATIONS).map(([gen, data]) => (
                      <SelectItem key={gen} value={gen} data-testid={`gen-option-${gen}`}>
                        {t('generation')} {gen} ({data.name})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                {/* Form Filter (Mega/Regional) */}
                <Select value={formFilter} onValueChange={setFormFilter}>
                  <SelectTrigger data-testid="form-filter" className="h-11">
                    <SelectValue placeholder={language === 'es' ? 'Formas' : 'Forms'} />
                  </SelectTrigger>
                  <SelectContent className="z-50">
                    <SelectItem value="all" data-testid="form-option-all">
                      {language === 'es' ? 'Todas las Formas' : 'All Forms'}
                    </SelectItem>
                    <SelectItem value="regular" data-testid="form-option-regular">
                      {language === 'es' ? 'Solo Regulares' : 'Regular Only'}
                    </SelectItem>
                    <SelectItem value="mega" data-testid="form-option-mega">
                      <span className="flex items-center gap-1">
                        <Zap className="w-3 h-3 text-yellow-500" />
                        {language === 'es' ? 'Mega Evoluciones' : 'Mega Evolutions'}
                      </span>
                    </SelectItem>
                    <SelectItem value="regional" data-testid="form-option-regional">
                      <span className="flex items-center gap-1">
                        <MapPin className="w-3 h-3 text-cyan-500" />
                        {language === 'es' ? 'Formas Regionales' : 'Regional Forms'}
                      </span>
                    </SelectItem>
                    <SelectItem value="alola" data-testid="form-option-alola">
                      <span className="flex items-center gap-1 pl-4">
                        <span className="w-2 h-2 rounded-full bg-yellow-500" />
                        Alola
                      </span>
                    </SelectItem>
                    <SelectItem value="galar" data-testid="form-option-galar">
                      <span className="flex items-center gap-1 pl-4">
                        <span className="w-2 h-2 rounded-full bg-rose-500" />
                        Galar
                      </span>
                    </SelectItem>
                    <SelectItem value="hisui" data-testid="form-option-hisui">
                      <span className="flex items-center gap-1 pl-4">
                        <span className="w-2 h-2 rounded-full bg-cyan-600" />
                        Hisui
                      </span>
                    </SelectItem>
                    <SelectItem value="paldea" data-testid="form-option-paldea">
                      <span className="flex items-center gap-1 pl-4">
                        <span className="w-2 h-2 rounded-full bg-violet-500" />
                        Paldea
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
                
                {/* Sort by */}
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger data-testid="sort-filter" className="h-11">
                    <SelectValue placeholder={t('sortBy')} />
                  </SelectTrigger>
                  <SelectContent className="z-50">
                    <SelectItem value="number">{t('number')}</SelectItem>
                    <SelectItem value="name">{t('name')}</SelectItem>
                    <SelectItem value="stats">{t('totalStats')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default AdvancedSearchBar;
